// =============================================================================
// Zakładka Serwery druku — karty agentów `print_agents` (wzorzec DevicesPage:
// karty zamiast tabeli, bo wierszy jest kilka, a statusu dużo).
//
// Parowanie idzie SZEŚCIOMA CYFRAMI — tak samo jak tablet, telewizor
// i stanowisko produkcyjne (migracja 182, tutaj 629). Poprzednio był to
// bootstrap-JWT: ~450 znaków base64 do przepisania z ekranu panelu do
// instalatora na maszynie w hali. Serwer wydruku był jedynym urządzeniem
// organizacji, które tego wymagało.
//
// Krok „nazwa" i krok „kod" są ROZDZIELONE, bo kod żyje 15 minut, a droga do
// maszyny bywa dłuższa: wpis w rejestrze zostaje, a kod odnawia się przyciskiem
// na karcie — bez kasowania i zakładania serwera od nowa. Wpis bez kodu nie
// daje żadnego dostępu, więc taki „pusty" agent niczego nie otwiera.
//
// Modal polluje wiersz agenta aż do `active` — dowodem sparowania jest BAZA,
// nie optymizm panelu. Uchwyty odwołania (`*_jti`) nie są nawet SELECT-owane:
// panel nie może być słabszy niż `publicAgent()` serwera.
// =============================================================================

import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Modal,
  ModalFooter,
  Input,
  Spinner,
} from '@insolitum/ui';
import { Copy, KeyRound, Plus, RefreshCw, Server, ShieldOff } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../providers/AuthProvider';

import {
  KOMUNIKAT_BRAK_PRINT_CORE,
  komunikatLimituPlanu,
  PrintCoreError,
  kodParowaniaSerwera,
  odwolajSerwerDruku,
  printCoreDostepny,
  zarejestrujSerwerDruku,
  type KodParowaniaSerwera,
} from './printCoreApi';
import {
  OPIS_AGENTA,
  formatujCzasWzgledny,
  formatujDate,
  odliczanieDo,
  stanAgenta,
} from './status';
import { useAgents, useLicznikiZadan24h, type AgentWiersz } from './usePrintingData';

/** Rytm pollowania wiersza agenta w modalu enrollmentu. */
export const POLL_ENROLLMENT_MS = 5_000;

interface Props {
  /** Filtr ?site= — agenci dostaną `site_id` w Fazie 7 (migracja M7). */
  siteId: string | null;
  /** Rytm polla enrollmentu — nadpisywany wyłącznie w testach (determinizm bez fake timers). */
  pollMs?: number;
}

export function AgentsTab({ pollMs = POLL_ENROLLMENT_MS }: Props) {
  const { t } = useTranslation();
  const { currentOrganization, hasRole } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const admin = hasRole(['owner', 'admin']);
  const zapisMozliwy = printCoreDostepny();

  const { agenci, loading, blad, odswiez } = useAgents();
  const liczniki = useLicznikiZadan24h();

  // `false` = zamknięty, `null` = krok „nazwa" (nowy serwer), wiersz = od razu
  // krok „kod" (odnowienie kodu dla istniejącego wpisu).
  const [parowanie, setParowanie] = useState<AgentWiersz | null | false>(false);
  const [doOdwolania, setDoOdwolania] = useState<AgentWiersz | null>(null);
  const [odwoluje, setOdwoluje] = useState(false);
  const [bladAkcji, setBladAkcji] = useState<string | null>(null);
  // Zegar odświeżany co 30 s, żeby „ostatni kontakt X temu" nie zastygał.
  const [teraz, setTeraz] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTeraz(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const odwolaj = async () => {
    if (!orgId || !doOdwolania) return;
    setOdwoluje(true);
    setBladAkcji(null);
    try {
      await odwolajSerwerDruku(orgId, doOdwolania.id);
      setDoOdwolania(null);
      await odswiez();
    } catch (err) {
      setBladAkcji(err instanceof PrintCoreError ? err.message : 'Nie udało się odwołać serwera');
    } finally {
      setOdwoluje(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-aurora-muted">
          {t(
            'printing.agentsHint',
            'Serwer wydruku działa w Twojej sieci i łączy chmurę z drukarkami (model pull — bez otwierania portów).'
          )}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => void odswiez()} className="iconbtn" title="Odśwież">
            <RefreshCw className="w-5 h-5" />
          </button>
          {admin && (
            <Button
              onClick={() => setParowanie(null)}
              disabled={!zapisMozliwy}
              title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
            >
              <Plus className="w-4 h-4" />
              {t('printing.addAgent', 'Dodaj serwer druku')}
            </Button>
          )}
        </div>
      </div>

      {blad && (
        <Alert variant="error">
          {blad}{' '}
          <button onClick={() => void odswiez()} className="underline">
            {t('printing.retry', 'Spróbuj ponownie')}
          </button>
        </Alert>
      )}
      {bladAkcji && (
        <Alert variant="error" dismissible onDismiss={() => setBladAkcji(null)}>
          {bladAkcji}
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : agenci.length === 0 ? (
        <EmptyState
          variant="card"
          icon={<Server className="w-8 h-8" />}
          title={t('printing.noAgents', 'Brak serwerów druku')}
          description={t(
            'printing.noAgentsHint',
            'Jeden serwer na organizację wystarcza, o ile widzi drukarki sieciowo. Drukarki na sterowniku (USB) wymagają serwera na maszynie, która je widzi.'
          )}
          action={
            admin ? (
              <Button
                onClick={() => setParowanie(null)}
                disabled={!zapisMozliwy}
                title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
              >
                <Plus className="w-4 h-4" />
                {t('printing.addAgent', 'Dodaj serwer druku')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-4">
          {agenci.map((agent) => {
            const stan = stanAgenta(agent, teraz);
            const opis = OPIS_AGENTA[stan];
            const licznik = liczniki[agent.id];

            return (
              <div key={agent.id} className="card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-4">
                    <span className="avatar w-12 h-12 rounded-ids-lg">
                      <Server className="w-6 h-6 text-aurora-on-accent" />
                    </span>
                    <div>
                      <h3 className="text-aurora-text font-medium flex items-center gap-2">
                        <span aria-hidden>{opis.symbol}</span>
                        {agent.name}
                        <Badge variant={opis.wariant} size="sm">
                          {opis.etykieta}
                        </Badge>
                      </h3>
                      <p className="text-xs text-aurora-muted mt-1">
                        {stan === 'pending' ? (
                          t(
                            'printing.awaitingPairing',
                            'Czeka na sparowanie — wygeneruj kod i wpisz go na maszynie.'
                          )
                        ) : (
                          <>
                            {t('printing.lastContact', 'ostatni kontakt')}{' '}
                            {formatujCzasWzgledny(agent.last_seen_at, teraz)}
                          </>
                        )}
                      </p>
                      <p className="text-xs text-aurora-subtle mt-1">
                        {[
                          agent.hostname && `host: ${agent.hostname}`,
                          agent.platform,
                          agent.app_version && `v${agent.app_version}`,
                          agent.registered_at &&
                            `zarejestrowano ${formatujDate(agent.registered_at)}`,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </p>
                      <p className="text-xs text-aurora-subtle mt-1">
                        {t('printing.jobs24h', 'Zadania 24 h:')}{' '}
                        {licznik ? (
                          <>
                            <span className="text-green-400">{licznik.ok} ✓</span>
                            {' / '}
                            <span className="text-red-400">{licznik.bledy} ✗</span>
                          </>
                        ) : (
                          '0'
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {admin && stan !== 'revoked' && (
                      // Dotyczy też serwera JUŻ sparowanego: przeinstalowana
                      // maszyna dostaje nowy kod, a stary token gaśnie w chwili
                      // użycia kodu (migracja 629) — bez zakładania wpisu od nowa.
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setParowanie(agent)}
                        disabled={!zapisMozliwy}
                        title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
                      >
                        <KeyRound className="w-4 h-4" />
                        {stan === 'pending'
                          ? t('printing.pairingCode', 'Kod parowania')
                          : t('printing.pairingCodeAgain', 'Nowy kod')}
                      </Button>
                    )}
                    {admin && stan !== 'revoked' && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setDoOdwolania(agent)}
                        disabled={!zapisMozliwy}
                        title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
                      >
                        <ShieldOff className="w-4 h-4" />
                        {t('printing.revoke', 'Odwołaj dostęp')}
                      </Button>
                    )}
                  </div>
                </div>
                {/* Tożsamość diagnostyczna — identyfikatory, NIGDY tokeny/jti. */}
                <div className="mt-3 pt-3 border-t border-aurora-border grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-aurora-subtle">ID serwera: </span>
                    <span className="mono text-aurora-muted break-all">{agent.id}</span>
                  </div>
                  <div>
                    <span className="text-aurora-subtle">ID urządzenia: </span>
                    <span className="mono text-aurora-muted break-all">
                      {agent.device_id ?? '—'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {parowanie !== false && orgId && (
        <ModalParowania
          orgId={orgId}
          agent={parowanie}
          pollMs={pollMs}
          onZamknij={() => {
            setParowanie(false);
            void odswiez();
          }}
        />
      )}

      {/* Potwierdzenie odwołania — z uczciwym opisem skutku (sweeper ~15 min). */}
      {doOdwolania && (
        <Modal
          open
          onClose={() => setDoOdwolania(null)}
          title={t('printing.revokeTitle', 'Odwołać dostęp serwera?')}
          footer={
            <ModalFooter>
              <Button variant="secondary" onClick={() => setDoOdwolania(null)}>
                {t('printing.cancel', 'Anuluj')}
              </Button>
              <Button variant="danger" onClick={() => void odwolaj()} loading={odwoluje}>
                {t('printing.revokeConfirm', 'Odwołaj')}
              </Button>
            </ModalFooter>
          }
        >
          <Alert variant="warning">
            {t(
              'printing.revokeWarning',
              'Odwołanie natychmiast odcina serwer „{{name}}". Zadania w locie wrócą do kolejki po ok. 15 minutach — o ile serwer nie zdążył ich wydrukować.',
              { name: doOdwolania.name }
            )}
          </Alert>
        </Modal>
      )}
    </div>
  );
}

// ── Modal parowania (nazwa → sześć cyfr) ─────────────────────────────────────

interface ModalParowaniaProps {
  orgId: string;
  /** `null` = zacznij od nazwy; wiersz = od razu wystaw kod dla tego wpisu. */
  agent: AgentWiersz | null;
  pollMs: number;
  onZamknij: () => void;
}

/** Sześć cyfr czyta się parami trójek — tak samo jak w aplikacji mobilnej. */
export function grupujKod(kod: string): string {
  return /^\d{6}$/.test(kod) ? `${kod.slice(0, 3)} ${kod.slice(3)}` : kod;
}

function ModalParowania({ orgId, agent, pollMs, onZamknij }: ModalParowaniaProps) {
  const { t } = useTranslation();
  const [nazwa, setNazwa] = useState('');
  const [pracuje, setPracuje] = useState(false);
  const [bladKroku, setBladKroku] = useState<string | null>(null);
  const [wpis, setWpis] = useState<{ id: string; name: string } | null>(
    agent ? { id: agent.id, name: agent.name } : null
  );
  const [kod, setKod] = useState<KodParowaniaSerwera | null>(null);
  const [skopiowano, setSkopiowano] = useState(false);
  const [polaczonyHost, setPolaczonyHost] = useState<string | null>(null);
  const [teraz, setTeraz] = useState(() => Date.now());

  // Zegar sekundowy odliczania TTL — tylko gdy kod jest na ekranie.
  useEffect(() => {
    if (!kod) return;
    const id = setInterval(() => setTeraz(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [kod]);

  // Poll wiersza agenta aż `active`: sukces parowania potwierdza BAZA
  // (maszyna wymieniła kod na token), nie optymizm panelu.
  useEffect(() => {
    if (!wpis || !kod || polaczonyHost !== null) return;
    const id = setInterval(() => {
      void supabase
        .from('print_agents')
        .select('id,status,hostname')
        .eq('id', wpis.id)
        .eq('organization_id', orgId)
        .maybeSingle()
        .then(({ data }) => {
          const wiersz = data as { status: string; hostname: string | null } | null;
          if (wiersz?.status === 'active') {
            setPolaczonyHost(wiersz.hostname ?? '');
          }
        });
    }, pollMs);
    return () => clearInterval(id);
  }, [wpis, kod, polaczonyHost, orgId, pollMs]);

  // Stabilna referencja (same settery w ciele) — dzięki temu efekt niżej ma
  // uczciwą listę zależności, bez wyciszania reguły.
  const wystawKod = useCallback(async (id: string) => {
    setPracuje(true);
    setBladKroku(null);
    try {
      setKod(await kodParowaniaSerwera(id));
      setSkopiowano(false);
    } catch (err) {
      setBladKroku(
        err instanceof PrintCoreError ? err.message : 'Nie udało się wygenerować kodu parowania'
      );
    } finally {
      setPracuje(false);
    }
  }, []);

  // Wpis podany z zewnątrz („Nowy kod" na karcie) — kod wystawiamy od razu,
  // bez pustego ekranu z jednym przyciskiem.
  useEffect(() => {
    if (agent) void wystawKod(agent.id);
  }, [agent, wystawKod]);

  const utworz = async () => {
    if (!nazwa.trim()) {
      setBladKroku('Nazwa serwera jest wymagana');
      return;
    }
    setPracuje(true);
    setBladKroku(null);
    try {
      const { agent: utworzony } = await zarejestrujSerwerDruku(orgId, nazwa.trim());
      setWpis({ id: utworzony.id, name: utworzony.name });
      await wystawKod(utworzony.id);
    } catch (err) {
      // 409 agent_exists → czytelny błąd pola zamiast surowego kodu.
      if (err instanceof PrintCoreError && err.kod === 'agent_exists') {
        setBladKroku('Serwer o tej nazwie już istnieje w organizacji');
      } else {
        setBladKroku(
          komunikatLimituPlanu(err) ??
            (err instanceof PrintCoreError ? err.message : 'Nie udało się utworzyć serwera')
        );
      }
    } finally {
      setPracuje(false);
    }
  };

  const kopiuj = () => {
    if (!kod) return;
    void navigator.clipboard?.writeText(kod.code);
    setSkopiowano(true);
    setTimeout(() => setSkopiowano(false), 1500);
  };

  const odliczanie = kod ? odliczanieDo(kod.expires_at, teraz) : null;
  const tytul = kod
    ? t('printing.pairStep2', 'Wpisz kod na serwerze wydruku')
    : t('printing.pairStep1', 'Nowy serwer druku');

  return (
    <Modal
      open
      onClose={onZamknij}
      title={tytul}
      size="lg"
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onZamknij}>
            {kod ? t('printing.close', 'Zamknij') : t('printing.cancel', 'Anuluj')}
          </Button>
          {!wpis && (
            <Button onClick={() => void utworz()} loading={pracuje}>
              {t('printing.createAgent', 'Utwórz')}
            </Button>
          )}
          {wpis && kod && !odliczanie && polaczonyHost === null && (
            <Button onClick={() => void wystawKod(wpis.id)} loading={pracuje}>
              {t('printing.newCode', 'Wygeneruj nowy kod')}
            </Button>
          )}
        </ModalFooter>
      }
    >
      {!wpis ? (
        <div className="space-y-4">
          {bladKroku && <Alert variant="error">{bladKroku}</Alert>}
          <div className="field">
            <label className="field__label">{t('printing.agentName', 'Nazwa serwera')}</label>
            <Input
              value={nazwa}
              onChange={(e) => setNazwa(e.target.value)}
              placeholder="np. paczkarnia-pc1"
            />
          </div>
          <p className="text-xs text-aurora-muted">
            {t(
              'printing.pairHint',
              'Po utworzeniu dostaniesz sześciocyfrowy kod (ważny 15 minut). Wpisujesz go w aplikacji Insolitum Cloud Print na maszynie, która widzi drukarki — tak samo jak przy parowaniu tabletu.'
            )}
          </p>
        </div>
      ) : polaczonyHost !== null ? (
        <Alert variant="success">
          {t('printing.pairDone', 'Serwer połączony ✓')}
          {polaczonyHost ? ` — ${polaczonyHost}` : ''}
        </Alert>
      ) : (
        <div className="space-y-4">
          {bladKroku && <Alert variant="error">{bladKroku}</Alert>}

          {/* Kod jest tu najważniejszy i tak ma wyglądać: duży, monospace,
              rozbity na trójki. To jedyna rzecz, którą człowiek przepisuje. */}
          <div className="rounded-ids-lg border border-aurora-border bg-aurora-bg-subtle p-6 text-center">
            {pracuje && !kod ? (
              <Spinner />
            ) : (
              <>
                <p className="mono text-4xl sm:text-5xl tracking-widest text-aurora-text">
                  {kod ? grupujKod(kod.code) : '— — —'}
                </p>
                <p className="text-xs text-aurora-muted mt-3">
                  {odliczanie ? (
                    <>
                      {t('printing.codeTtl', 'Kod wygasa za')}{' '}
                      <span className="mono">{odliczanie}</span>
                    </>
                  ) : (
                    t('printing.codeExpired', 'Kod wygasł — wygeneruj nowy.')
                  )}
                </p>
              </>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Button variant="secondary" size="sm" onClick={kopiuj} disabled={!kod}>
              <Copy className="w-4 h-4" />
              {skopiowano
                ? t('printing.copied', 'Skopiowano')
                : t('printing.copyCode', 'Kopiuj kod')}
            </Button>
            <p className="text-xs text-aurora-subtle">
              {t('printing.serverName', 'Serwer')}: <span className="mono">{wpis.name}</span>
            </p>
          </div>

          {/* GDZIE TEN KOD WPISAĆ.

              Do 0.3.0 pytał o niego KREATOR INSTALATORA, obok adresu print-core —
              i to było źródłem zgłoszenia „nie ma ikony, nic nie widać, a panel
              dalej czeka na parowanie". Instalacja kończyła się bez okna,
              bez ikony w zasobniku i bez informacji, czy parowanie się udało;
              gdy adres był zły, jedynym objawem był ten modal, wiszący
              w nieskończoność.

              Od 0.4.0 kod wpisuje się w OKNIE aplikacji, po instalacji, a adresu
              nie podaje się wcale — aplikacja zna go z kanonu (`ADRES_PRINT_CORE`).
              Ten blok mówi więc, gdzie iść, a nie co przepisać. */}
          <div className="text-xs text-aurora-muted space-y-1">
            <p>
              {t(
                'printing.appFields',
                'Na maszynie z drukarkami otwórz aplikację Insolitum Cloud Print (ikona w zasobniku systemowym) i wpisz w niej ten kod.'
              )}
            </p>
            <p>
              <span className="text-aurora-subtle">
                {t('printing.installerCode', 'Kod parowania:')}
              </span>{' '}
              <span className="mono">{kod?.code ?? '—'}</span>
            </p>
            <p className="text-aurora-subtle">
              {t('printing.appNoUrl', 'Adresu serwera nie podaje się — aplikacja zna go sama.')}
            </p>
          </div>

          {odliczanie && (
            <div className="flex items-center gap-2 text-xs text-aurora-muted">
              <Spinner size="sm" />
              {t('printing.waitingForAgent', 'Czekam na połączenie serwera…')}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
